import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { materialCategoryService } from '../../../services/materialCategoryService';

const CategoryDetailPanel = ({ category, onEdit, onDelete }) => {
  const [categoryPath, setCategoryPath] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [materialCount, setMaterialCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      loadCategoryDetails();
    }
  }, [category]);

  const loadCategoryDetails = async () => {
    setLoading(true);
    
    // Load category path (breadcrumb)
    const { data: path } = await materialCategoryService?.getCategoryPath(category?.id);
    setCategoryPath(path || []);

    // Load child categories
    const { data: children } = await materialCategoryService?.getCategoriesByParent(category?.id);
    setChildCategories(children || []);

    // Load material count
    const { data: count } = await materialCategoryService?.getMaterialCountByCategory(category?.id);
    setMaterialCount(count || 0);

    setLoading(false);
  };

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <Icon name="Info" size={48} color="var(--color-muted-foreground)" />
        <p className="mt-4 text-sm text-muted-foreground text-center">
          Select a category from the tree to view details
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">{category?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">Level {category?.level} Category</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(category)}
              className="p-2 hover:bg-muted rounded-lg transition-colors duration-250"
              title="Edit category"
            >
              <Icon name="Edit2" size={18} color="var(--color-muted-foreground)" />
            </button>
            <button
              onClick={() => onDelete(category?.id)}
              className="p-2 hover:bg-destructive/10 rounded-lg transition-colors duration-250"
              title="Delete category"
            >
              <Icon name="Trash2" size={18} color="var(--color-destructive)" />
            </button>
          </div>
        </div>

        {/* Breadcrumb Path */}
        {categoryPath?.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {categoryPath?.map((pathItem, index) => (
              <React.Fragment key={pathItem?.id}>
                <span className="text-xs text-muted-foreground">{pathItem?.name}</span>
                {index < categoryPath?.length - 1 && (
                  <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
              <p className="text-sm text-muted-foreground">
                {category?.description || 'No description provided'}
              </p>
            </div>

            {/* Material Attributes - Enhanced Display */}
            <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Material Attributes</h3>
                {category?.material_attributes ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
                    <Icon name="CheckCircle2" size={18} color="#22c55e" />
                    <span className="text-sm font-semibold text-green-600">Enabled</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-muted border border-border rounded-full">
                    <Icon name="XCircle" size={18} color="var(--color-muted-foreground)" />
                    <span className="text-sm font-medium text-muted-foreground">Disabled</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {category?.material_attributes 
                  ? 'This category references attributes from the level above it' :'This category does not reference parent level attributes'}
              </p>
            </div>

            {/* Statistics */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Statistics</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Linked Materials</p>
                  <p className="text-xl font-semibold text-foreground mt-1">{materialCount}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Subcategories</p>
                  <p className="text-xl font-semibold text-foreground mt-1">{childCategories?.length}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Category Level</p>
                  <p className="text-xl font-semibold text-foreground mt-1">{category?.level}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Display Order</p>
                  <p className="text-xl font-semibold text-foreground mt-1">{category?.display_order}</p>
                </div>
              </div>
            </div>

            {/* Child Categories */}
            {childCategories?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">Subcategories</h3>
                <div className="space-y-2">
                  {childCategories?.map(child => (
                    <div
                      key={child?.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="Folder" size={16} color="var(--color-primary)" />
                        <span className="text-sm text-foreground">{child?.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Level {child?.level}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Metadata</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Category ID:</span>
                  <span className="text-foreground font-mono text-xs">{category?.id?.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="text-foreground">
                    {new Date(category?.created_at)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Updated:</span>
                  <span className="text-foreground">
                    {new Date(category?.updated_at)?.toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryDetailPanel;
